import { ACTION_DEFS } from './actions.js';
import { ALL_ROLES } from './roles.js';
/**
 * Tabulkova definice politiky (ADR-07: "tabulkova definice, testovana jako
 * tabulka"). Pro kazdou roli explicitni allowlist akci - nic implicitniho.
 *
 * Pravidla nad ramec teto tabulky resi engine (v poradi):
 *  1. tenant match (cizi/neexistujici zdroj => 404 semantics, nikoli 403),
 *  2. rozsah role ROLE_REACH narovnany vztahem ke zdroji (ABAC:
 *     ucastnik case / prirazeny uzivatel / vlastnik),
 *  3. klasifikace sensitive_identity jen pro SENSITIVE_READ_ROLES,
 *  4. service principaly rozhoduji scope tokeny PAT.
 */
const ALL_ACTION_NAMES = ACTION_DEFS.map((a) => a.name);
function actions(...names) {
    return names.flatMap((n) => (typeof n === 'string' ? [n] : [...n]));
}
/** Role smi vse - vyjimky by se tu musely vypisovat, zadne nejsou. */
const OWNER_ADMIN = ALL_ACTION_NAMES;
/**
 * Allowlisty roli. Upravuj po radcich; matice v testech musi zustat
 * ve shode (dvojity zaznam odhalí nechtene zmeny).
 */
export const ROLE_ACTIONS = {
    owner: OWNER_ADMIN,
    admin: OWNER_ADMIN,
    finance: actions([
        'party.profile.read',
        'party.profile.update',
        'property.property.read',
        'case.case.read',
        'documents.document.read',
        'documents.document.download',
        'documents.document.upload',
        'finance.commission.read',
        'finance.commission.create',
        'finance.commission.update',
        'finance.invoice.create',
        // QAFIX6 (N02): cteni faktur ma stejny rozsah jako jejich vystaveni a
        // odeslani - doklady kancelare, ne „jen svoje" (service nad `invoices`
        // zadne per-maklerske zuzeni nema, filtruje jen RLS tenanta). Makler ma
        // svoje doklady pod `finance.client_invoice.manage_own` (FAK2).
        'finance.invoice.read',
        'finance.invoice.send',
        'finance.payment.read',
        'finance.payment.create',
        'finance.payment.approve',
        'finance.refund.create',
        'finance.escrow.create',
        'finance.escrow.update',
        'finance.report.read',
        'finance.report.export',
        'finance.databox.send',
        'finance.cost.read',
        'finance.cost.create',
        'finance.cost.update',
        'finance.cost.delete',
        'finance.cost_summary.read',
        'finance.billing_settings.read',
        'finance.supplier.manage',
        'finance.client_invoice.manage',
        'finance.service_catalog.manage',
        'finance.service_catalog.read',
        // Krok FAK3: ucetni oddeleni vede predpisy najmu, mesicni davku,
        // uhrady i rocni vyuctovani sluzeb.
        'finance.rent.manage',
        'finance.rent.read',
        'pm.lease.read',
        'pm.rent_invoice.issue',
        'pm.ticket.read',
        'communication.email.send',
        'communication.message.create',
        'communication.message.read',
        'communication.thread.read',
        'communication.domain.read',
        'communication.template.read',
        // Krok C1: ucetni oddeleni vidi cenik i objednavky reportu a smi je
        // zalozit; rucni potvrzeni platby a refundace zustavaji owner/admin.
        'commerce.product.read',
        'commerce.order.read',
        'commerce.order.create',
    ]),
    legal: actions([
        'party.profile.read',
        'party.profile.update',
        'party.consent.update',
        'party.profile.sensitive_read',
        'property.property.read',
        'case.case.read',
        'case.case.update',
        'case.case.close',
        'documents.document.read',
        'documents.document.download',
        'documents.document.upload',
        'documents.document.sign_request',
        'documents.template.manage',
        // Krok T3: pravni oddeleni schvaluje sablony kancelare a smlouvy.
        'documents.contract.read',
        'documents.contract.manage',
        'documents.contract_template.read',
        'documents.contract_template.manage',
        // Krok KP1: pravni oddeleni vidi podani klientu (AML identifikace) a smi
        // pozvanku zalozit i zneplatnit.
        'documents.client_intake.read',
        'documents.client_intake.manage',
        // Krok ES1: pravnicky o sobe rozhodne, kdo smlouvu podepise a kterou
        // cestou (uroven podpisu je pravni ukol, ne kancelarska volba).
        'documents.signature.read',
        'documents.signature.manage',
        'documents.signature.sign',
        'documents.signature.configure',
        'finance.databox.send',
        'identity.auditlog.read',
        'audit.log.read',
        'audit.chain.verify',
        'communication.email.send',
        'communication.message.create',
        'communication.message.read',
        'communication.thread.read',
        'communication.thread.update',
        'communication.domain.read',
        'communication.template.read',
        // Rozhodovani ve schvalovacich workflow (krok 10): pravni oddeleni.
        'workflow.approval.decide',
    ]),
    broker: actions([
        'party.profile.read',
        'party.profile.create',
        'party.profile.update',
        'party.consent.update',
        'property.property.read',
        'property.property.create',
        'property.property.update',
        'property.property.export',
        'property.valuation.write',
        'property.listing.publish',
        'property.listing.unpublish',
        'case.case.read',
        'case.case.create',
        'case.case.update',
        'case.case.assign',
        'case.case.close',
        'case.case.export',
        'documents.document.read',
        'documents.document.download',
        'documents.document.upload',
        'documents.document.update',
        // Krok T3: makler vystavuje a upravuje smlouvy sve zakazky, muze zalozit
        // i vlastni sablonu kancelare (import + mapovani).
        'documents.contract.read',
        'documents.contract.manage',
        // Krok RBAC1: katalog a detail vzoru kancelare makler jen CTE (zaklada z
        // nich smlouvu); `manage` ma dal kvuli importu vlastniho mustru (T3).
        'documents.contract_template.read',
        'documents.contract_template.manage',
        // Krok KP1: makler posila klientovi odkaz na formular a potvrzuje prevzeti.
        'documents.client_intake.read',
        'documents.client_intake.manage',
        // Krok ES1: makler posila stranam odkaz k podpisu a hlida jejich stav.
        // Rozhodnuti, JAKYM podpisem se smlouva podepise, je u typu s
        // katastralnim ucinkem pravni vec — `signature.sign` ma jen legal.
        'documents.signature.read',
        'documents.signature.manage',
        'finance.commission.read',
        'finance.cost.read',
        'finance.cost.create',
        'finance.cost.update',
        'finance.cost.delete',
        // Krok RBAC1: souhrn nakladu zakazky (kanban i detail) — castky si API
        // maskuje samo (`canViewCommercialTerms`), pravo na souhrn tedy neni
        // pravo na cizi provize.
        'finance.cost_summary.read',
        // Krok FAK1: makler v rezimu 'broker' spravuje jen VLASTNI dodavatel
        // (scope hlida service vrstva); nastaveni "kdo fakturuje" jen cte.
        'finance.billing_settings.read',
        'finance.supplier.manage_own',
        'finance.client_invoice.manage_own',
        'finance.service_catalog.read',
        // Krok FAK3: makler spravuje jen najmy fakturovane pres VLASTNIHO
        // dodavatele (scope hlida `rentVisibilityScope`).
        'finance.rent.manage_own',
        'finance.rent.read',
        'pm.lease.read',
        'communication.email.send',
        'communication.message.create',
        'communication.message.read',
        'communication.thread.read',
        'communication.thread.create',
        'communication.thread.update',
        'communication.domain.read',
        'communication.template.read',
        'communication.template.create',
        'communication.case.create',
        'communication.unsubscribe.create',
        'acquisition.lead.create',
        'acquisition.lead.read',
        'acquisition.lead.update',
        'acquisition.lead.delete',
        'acquisition.audit.create',
        'acquisition.audit.read',
        'acquisition.seller_pack.create',
        // Krok CRM1: pipeline poptavek. Makler vede poptavky od prvniho ohlasu
        // po smlouvu; `delete` ma jen on (a vedouci) - smazana poptavka bere s
        // sebou i historii komunikace, asistentka ji jen nemaze.
        'crm.demand.read',
        'crm.demand.create',
        'crm.demand.update',
        'crm.demand.delete',
        'crm.demand.import',
        // Krok CRM2: ukoly maklere. Makler ma vlastni tabuli ukolu a vidi i
        // automaticke ukoly vznikle z bran auditu, terminu a faktur - ty musi
        // umet odskaknout, jinak by visely ve sloupci "K udelani" navzdy. Spusteni
        // generatoru (`crm.task.generate`) je vedouci, ne makler.
        'crm.task.read',
        'crm.task.create',
        'crm.task.update',
        'crm.task.delete',
        // Krok CRM3: kalendar je denni chleba maklera - prohlidky, podpisy a
        // schuzky si vede sam a je to jedina obrazovka, kde se da zjistit, ze
        // nekdo neni makler. Odběr do Google kalendáře si taky spravuje sam
        // (je to jeho odkaz, ne nastaveni kanceláře). Odeslání připomínek po
        // celé kanceláři (`crm.calendar_reminder.send`) je vedoucí.
        'crm.calendar_event.read',
        'crm.calendar_event.create',
        'crm.calendar_event.update',
        'crm.calendar_event.delete',
        'crm.calendar_feed.manage',
        // Krok CRM4: prohlidky a parovani jsou makleruv obchod - vypise terminy,
        // posle zajemci odkaz, vidi shody a posle nabidku. Obe odeslani jsou
        // `communicate` (v UI vzdy po nahledu a potvrzeni).
        'crm.viewing.read',
        'crm.viewing.manage',
        'crm.viewing.invite',
        'crm.match.read',
        'crm.match.update',
        'crm.offer.send',
        // Krok NOTIF1: svoji frontu upozorneni cte a oznacuje prectenou kazdy
        // sam (cizi frontu nikdo nevidi - `user_id` je prihlaseny uzivatel).
        'crm.notification.read',
        'crm.notification.update',
        'dd.case.create',
        'dd.case.read',
        'dd.case.update',
        'dd.case.manage_participants',
        'dd.fact.record',
        'dd.question.manage',
        'dd.claim.record',
        // R.U. 1 (Oprava 1 kroku CRM1): rucne vedena ocekavaci tabulka
        // (`test/helpers/expected-allow.ts`) brokera u `dd.geometry.lock` pustila
        // od zacatku, tato tabulka ne - drift z kroku "audit faze 0+1" (f2e10253).
        // Rozhodla realita, ne odhad: hlavni tok, ktery geometrii fakticky zamyka
        // (`POST /v1/duediligence/cases/:id/identify`), se autorizuje akci
        // `dd.research.run`, kterou broker MA. Bez tohoto radku tedy makler zamkl
        // geometrii rychlejsi cestou, ale na primem zapisu (`POST .../geometry`,
        // MCP `dd.lock_geometry`) dostal 403 - rozpor v jedne operaci.
        // Zapis je porad omezeny ABAC (`isRelated`): makler jen u pripadu, na
        // ktery je ucastnik nebo ho vlastni - drzi to
        // duediligence/test/case-participants.int.spec.ts.
        'dd.geometry.lock',
        'dd.research.run',
        'dd.research.fetch_open',
        // Krok A4: dotazy spravcum siti, SVJ a obci - makler(ka) rozhoduje o jejich
        // odeslani (odchazi jmenem kancelare) a smi je zamitnout s duvodem.
        'dd.correspondence.approve',
        'dd.correspondence.reject',
        // Krok C1: makler objednava report na nemovitost sve kancelare
        // (z kreditu tarifu nebo platbou).
        'commerce.product.read',
        'commerce.order.read',
        'commerce.order.create',
    ]),
    assistant: actions([
        'party.profile.read',
        'party.profile.create',
        'party.profile.update',
        'party.consent.update',
        'property.property.read',
        'case.case.read',
        'case.case.create',
        'case.case.update',
        'documents.document.read',
        'documents.document.download',
        'documents.document.upload',
        'documents.document.update',
        // Krok T3: asistentka pripravuje/upravuje smlouvy za maklere.
        'documents.contract.read',
        'documents.contract.manage',
        // Krok RBAC1: aby smlouvu pripravila, musi videt katalog i detail vzoru
        // kancelare; menit mustry nesmi (`contract_template.manage` nema).
        'documents.contract_template.read',
        // Krok KP1: asistentka pozvanku posila a hlida stav; prevzeti udaju
        // do smlouvy je taky jeji bezna prace (makler ji o to zada).
        'documents.client_intake.read',
        'documents.client_intake.manage',
        // Krok ES1: asistentka hlida stav podpisu a posle pripominku, ale
        // NEODESILA podepisovy odkaz ani nepodepisuje (to je pravni akt).
        'documents.signature.read',
        'communication.email.send',
        'communication.message.create',
        'communication.message.read',
        'communication.thread.read',
        'communication.thread.create',
        'communication.thread.update',
        'communication.case.create',
        'communication.unsubscribe.create',
        'acquisition.lead.create',
        'acquisition.lead.read',
        'acquisition.lead.update',
        'acquisition.audit.create',
        'acquisition.audit.read',
        'acquisition.seller_pack.create',
        // Krok CRM1: asistentka pripravuje poptavku (zaznamenej ohlas, zaloz
        // poptavku, pridej poznamku) a nestiri sloupce pipeline - to je
        // nastaveni kancelare (`crm.demand_stage.manage`), ne jeji prace.
        'crm.demand.read',
        'crm.demand.create',
        'crm.demand.update',
        'crm.demand.import',
        // Krok CRM2: asistentka zaklada a posouva ukoly, ale nesmava je mazat -
        // to je prace maklera, ktery ukol vytvoril nebo ho ma v peci.
        'crm.task.read',
        'crm.task.create',
        'crm.task.update',
        // Krok CRM3: asistentka zaklada a presouva udalosti v kalendari (typicky
        // prohlidku, kdyz klient objedna), ale nema vlastni odběr do Google
        // kalendáře (`calendar_feed.manage`) - ten patří maklerovi, jehož
        // kalendář se odbírá.
        'crm.calendar_event.read',
        'crm.calendar_event.create',
        'crm.calendar_event.update',
        // Krok CRM4: asistentka vypisuje terminy prohlidek a vidi shody, ale
        // klientovi sama NIC neposila (pozvanka ani nabidka) - odeslani klientovi
        // je rozhodnuti maklere, ktery obchod vede.
        'crm.viewing.read',
        'crm.viewing.manage',
        'crm.match.read',
        // Krok NOTIF1: asistentka ma svou frontu upozorneni stejne jako makler
        // (zakazky, ktere vede, jsou taky jeji prace).
        'crm.notification.read',
        'crm.notification.update',
        'dd.case.create',
        'dd.case.read',
        'dd.case.update',
        'dd.case.manage_participants',
        'dd.fact.record',
        'dd.question.manage',
        'dd.research.run',
        'dd.research.fetch_open',
        // Krok A4: asistentka dotazy odesila i zamita - je to bezna kancelarska
        // korespondence, ne pravni ani financni akt.
        'dd.correspondence.approve',
        'dd.correspondence.reject',
        // Krok C1: asistentka vidi cenik a stav objednavek, ale neobjednava
        // (objednavka je vydaj - to je rozhodnuti maklere/vedeni).
        'commerce.product.read',
        'commerce.order.read',
    ]),
    pm_manager: actions([
        'party.profile.read',
        'party.profile.create',
        'party.profile.update',
        'property.property.read',
        'property.property.update',
        // R3 (docs/agent-briefs/REPORT-reos-r3-ownership.md): pm_manager edituje
        // zakazky sve kancelare i kdyz neni vlastnik (ROLE_REACH office).
        'case.case.read',
        'case.case.update',
        'documents.document.read',
        'documents.document.download',
        'documents.document.upload',
        // Krok T3: vedouci spravy nemovitosti vidi smlouvy najmu sve kancelare.
        'documents.contract.read',
        // Krok RBAC1: vzory najemnich smluv kancelare cte (zaklada z nich najmy).
        'documents.contract_template.read',
        // Krok KP1: vedouci spravy vidi stav pozvanek najemcu (ne citliva data -
        // ta z API nikdy neodchazi), ale odkazy neposila.
        'documents.client_intake.read',
        // Krok ES1: vedouci spravy vidi stav podpisu (nez cast podepisovani).
        'documents.signature.read',
        'finance.payment.read',
        'finance.cost.read',
        'finance.cost.create',
        'finance.cost.update',
        'finance.cost.delete',
        'finance.cost_summary.read',
        'pm.lease.read',
        'pm.lease.create',
        'pm.lease.update',
        'pm.lease.terminate',
        'pm.ticket.read',
        'pm.ticket.create',
        'pm.ticket.update',
        'pm.ticket.close',
        'pm.maintenance_order.create',
        'pm.rent_invoice.issue',
        // Krok FAK3: sprava najmu je jadro prace pm_managera (predpisy, mesicni
        // davka, uhrady, vyuctovani sluzeb).
        'finance.rent.manage',
        'finance.rent.read',
        'communication.email.send',
    ]),
    // Read-only, bez downloadu a bez cehokoli financniho (exfil/IDOR opatrnost).
    viewer: actions([
        'party.profile.read',
        'property.property.read',
        'case.case.read',
        'documents.document.read',
        'documents.contract.read',
        'pm.lease.read',
        'pm.ticket.read',
        'dd.case.read',
        // Krok CRM1: poptavky jsou obchodni data kancelare (kontakt + hledane
        // parametry) - read-only je spravedlive, zbytek ne.
        'crm.demand.read',
        // Krok CRM2: ukoly taky jen cist. Je v nich odkaz na zakazku a klienta,
        // takze by to byly obchodni data, ne interni poznamka.
        'crm.task.read',
        // Krok CRM3: kalendar jen cist - je v nem vidět, kdo kdy má prohlidku,
        // coz je obchodni udaj o praci maklera, ne interni poznamka.
        'crm.calendar_event.read',
        // Krok CRM4: prohlidky a shody jen cist (stejny duvod jako kalendar).
        'crm.viewing.read',
        'crm.match.read',
    ]),
    // ---- portalove role: jen zdroje s primym vztahem (reach 'own') ----------
    seller: actions([
        'property.property.read',
        'case.case.read',
        'documents.document.read',
        'documents.document.download',
        'documents.document.sign_request',
        'communication.email.send',
    ]),
    buyer: actions([
        'property.property.read',
        'case.case.read',
        'documents.document.read',
        'documents.document.download',
        'documents.document.sign_request',
        'communication.email.send',
    ]),
    tenant: actions([
        'property.property.read',
        'pm.lease.read',
        'pm.ticket.read',
        'pm.ticket.create',
        'finance.payment.read',
        'finance.payment.create',
        'documents.document.read',
        'documents.document.download',
        'documents.document.sign_request',
        'communication.email.send',
    ]),
    owner_portal: actions([
        'property.property.read',
        'case.case.read',
        'documents.document.read',
        'documents.document.download',
        'finance.payment.read',
        'finance.report.read',
        'pm.lease.read',
        'pm.ticket.read',
        'communication.email.send',
    ]),
    developer: actions([
        'party.profile.read',
        'party.profile.create',
        'party.profile.update',
        'property.property.read',
        'property.property.create',
        'property.property.update',
        'property.listing.publish',
        'property.listing.unpublish',
        'case.case.read',
        'case.case.create',
        'case.case.update',
        'documents.document.read',
        'documents.document.download',
        'documents.document.upload',
        'pm.lease.read',
        'communication.email.send',
    ]),
};
// Fail-fast self-check: kazdy radek obsahuje jen zname akce a role.
for (const role of ALL_ROLES) {
    const list = ROLE_ACTIONS[role];
    if (!list)
        throw new Error(`ROLE_ACTIONS nema radku pro roli '${role}'`);
    for (const name of list) {
        if (!ALL_ACTION_NAMES.includes(name)) {
            throw new Error(`ROLE_ACTIONS[${role}] obsahuje neznamou akci '${name}'`);
        }
    }
}
//# sourceMappingURL=policies.js.map